<template>
  <div
    class="ai-chat-widget"
    :class="{ 'ai-chat-widget--expanded': expanded }"
    data-testid="ai-chat-widget"
    :data-session-id="sessionId"
  >
    <!-- 最小化時のアイコン -->
    <v-tooltip top :disabled="!disabledButton">
      <template v-slot:activator="{ on, attrs }">
        <div class="d-inline-block" v-on="on" v-bind="attrs" data-testid="ai-chat-minimized-tooltip-activator">
          <round-button
            ref="minimizedButton"
            class="ai-chat-widget__minimized"
            icon="mdi-robot"
            :disabled="disabledButton"
            @click="expand"
            v-show="!expanded && !isAnimating"
            :elevation="2"
            data-testid="ai-chat-minimized-button"
          />
        </div>
      </template>
      <span data-testid="ai-chat-minimized-tooltip">{{ tooltipText }}</span>
    </v-tooltip>

    <!-- 展開時のチャットウィンドウ -->
    <transition name="slide-up" @before-leave="handleBeforeLeave" @after-leave="handleAfterLeave">
      <div v-show="expanded" class="ai-chat-widget__window" data-testid="ai-chat-window">
        <div class="ai-chat-widget__header">
          <span class="ai-chat-widget__title" data-testid="ai-chat-title">{{chatTitle}} ({{selectionStatus}})</span>
          <div class="ai-chat-widget__controls">
            <v-btn
              ref="newSessionButton"
              icon
              small
              @click="newSession"
              :title="$t('new_session')"
              data-testid="ai-chat-new-session-button"
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
            <v-btn
              ref="minimizeButton"
              icon
              small
              @click="minimize"
              :title="$t('minimize')"
              data-testid="ai-chat-minimize-button"
            >
              <v-icon small>mdi-window-minimize</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="ai-chat-widget__history" ref="history">
          <div :class="`ai-chat-widget__${msg.type}`" v-for="msg in messages" :key="msg.timestamp">
            <div class="ai-chat-widget__message" :data-testid="getMessageTestId(msg.type)">{{msg.text}}</div>
          </div>
          <div :class="`ai-chat-widget__response`" v-if="waitingForResponse">
            <div class="ai-chat-widget__message">
              <v-progress-circular color="primary" indeterminate size="20" />
            </div>
          </div>
          <div class="ai-chat-widget__presets" v-if="shouldShowPresets" data-testid="ai-chat-presets">
            <v-chip
              class="mx-1 mb-1"
              small
              :disabled="!canSendMessage"
              @click="sendMessage(preset)"
              v-for="preset in $t('presets')"
              :key="preset"
            >
              {{ preset }}
            </v-chip>
          </div>
        </div>
        <div class="ai-chat-widget__footer">
          <textarea
            ref="aiChatInput"
            v-model="inputValue"
            class="ai-chat-widget__input"
            :placeholder="inputPlaceholder"
            :disabled="!canInputMessage"
            rows="1"
            @keydown="handleTextarea"
            @compositionstart="composing = true"
            @compositionend="composing = false"
            data-testid="ai-chat-input"
          >
          </textarea>
          <div class="ai-chat-widget__disclaimer">
            {{ $t('disclaimer') }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<i18n lang="yaml">
ja:
  title: 'AIチャット'
  selection_status: '{count}件選択中'
  new_session: '新しいセッション'
  minimize: '収納'
  data_changed_notification: 'データが変更されたので新しいセッションを始めます'
  select_data_first: 'データを選択してからご質問ください'
  responding: '回答中です...'
  disclaimer: 'この機能は不正確な情報を示すことがあります。生成された回答を再確認するようにしてください。'
  placeholder: '質問を入力してください'
  presets:
    - '捕獲数をランキング形式で表示して'
    - '月々の捕獲数を比較して'
    - '内部発生と外部侵入の割合を教えて'
  tooltip:
    crawlingNotSupported: 'この機能は床置きトラップのデータに対応していません'
    noDataSelected: 'データを選択してください'
  errors:
    please_try_again: 'エラーが発生しました。しばらくしてから再度お試しください。'

en:
  title: 'AI Chat'
  selection_status: '{count} selected'
  new_session: 'New Session'
  minimize: 'Minimize'
  data_changed_notification: 'Starting a new session because the selected data has changed'
  select_data_first: 'Please select data before asking questions'
  responding: 'Responding...'
  disclaimer: 'This feature may provide inaccurate information. Please verify the generated answers.'
  placeholder: 'Please enter your question'
  presets:
    - 'Display capture counts in ranking order'
    - 'Compare monthly capture counts'
    - 'Internal vs external incident ratio'
  tooltip:
    crawlingNotSupported: 'This feature does not support crawling pest data'
    noDataSelected: 'Please select data'
  errors:
    please_try_again: 'An error occurred. Please try again later.'
</i18n>

<script>
import { WebSocketConnection } from '@/libs/webSocketConnection';
import Notifications from '@/mixins/notifications';

import RoundButton from '@/components/atoms/RoundButton';

export default {
  name: 'ai-chat-widget',
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    place_id: String,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  inject: {
    createWebSocketConnection: {
      default: () => (url) => new WebSocketConnection(url),
    },
  },
  mixins: [
    Notifications,
  ],
  components: {
    RoundButton,
  },
  data() {
    return {
      expanded: false,
      connection: null,
      messages: [],
      streaming: false,
      composing: false,
      sessionId: null,
      isAnimating: false,
      pendingDataChange: false,
      inputValue: '',
    };
  },
  computed: {
    disabledButton() {
      return this.events.length === 0 || this.disabled;
    },
    selectionStatus() {
      return this.$t('selection_status', { count: this.events.length });
    },
    chatTitle() {
      return this.$t('title');
    },
    canInputMessage() {
      return this.events.length > 0 && !this.streaming && !this.disabled;
    },
    canSendMessage() {
      return this.events.length > 0 && !this.streaming && !this.composing && !this.disabled;
    },
    hasDataSelected() {
      return this.events.length > 0;
    },
    // 暫定: 徘徊性データは段階出荷中のため、本ボタンは徘徊性混在時に disabled としている。
    // Redmine #111558 (S床のデータでAIとチャットできる) 完了時に this.disabled 分岐を削除する。
    tooltipText() {
      if (this.disabled) {
        return this.$t('tooltip.crawlingNotSupported');
      }
      if (this.events.length === 0) {
        return this.$t('tooltip.noDataSelected');
      }
      return '';
    },
    inputPlaceholder() {
      // 暫定: 徘徊性データは段階出荷中のため、disabled 時の placeholder で徘徊性を断定している。
      // Redmine #111558 (S床のデータでAIとチャットできる) 完了時に this.disabled 分岐を削除する。
      if (this.disabled) {
        return this.$t('tooltip.crawlingNotSupported');
      }
      if (!this.hasDataSelected) {
        return this.$t('select_data_first');
      }
      if (this.streaming) {
        return this.$t('responding');
      }
      return this.$t('placeholder');
    },
    waitingForResponse() {
      const lastMessage = this.messages[this.messages.length - 1];
      const lastIsResponse = lastMessage && lastMessage.type === 'response';
      return this.streaming && !lastIsResponse;
    },
    websocketApiUrl() {
      const user = this.$store.getters.getCurrentUser;
      return user.websocket_api_url;
    },
    shouldShowPresets() {
      return this.messages.length <= 0 && this.inputValue.trim() === '';
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.applyFocusFix(this.$refs.minimizedButton.$el);
      this.applyFocusFix(this.$refs.aiChatInput);
      this.applyFocusFix(this.$refs.newSessionButton.$el);
      this.applyFocusFix(this.$refs.minimizeButton.$el);
    });
  },
  methods: {
    expand() {
      this.expanded = true;
      if (!this.connection) {
        this.connection = this.createWebSocketConnection(this.websocketApiUrl);
        this.connection.addMessageHandler(this.handleResponse);
      }
    },
    getMessageTestId(messageType) {
      if (messageType === 'system') {
        return 'ai-chat-system-message';
      }
      return 'ai-chat-message';
    },
    minimize() {
      this.expanded = false;
      // 注意: connection は閉じない、messages も保持
    },
    newSession() {
      // 既存接続をクローズ
      if (this.connection) {
        this.connection.close();
        this.connection = null;
      }

      // 状態リセット
      this.messages = [];
      this.sessionId = null;
      this.streaming = false;

      // 新しい接続を作成
      this.connection = this.createWebSocketConnection(this.websocketApiUrl);
      this.connection.addMessageHandler(this.handleResponse);
    },
    finishStreaming() {
      this.streaming = false;

      // ストリーミング完了後に保留中のデータ変更通知を処理
      if (this.pendingDataChange) {
        this.pendingDataChange = false;
        // 少し遅延させてから通知を表示
        this.$nextTick(() => {
          setTimeout(() => {
            this.startNewSessionWithNotification();
          }, 500);
        });
      }
    },
    handleResponse(event) {
      const data = JSON.parse(event.data);

      // 全てのメッセージでセッション検証を実行
      if (!this.isValidSessionResponse(data)) {
        return;
      }

      switch (data.type) {
        case 'stream_start':
          this.sessionId = data.session_id;
          break;
        case 'stream_chunk':
          this.updateLastResponse(data.message);
          break;
        case 'stream_end':
          this.finishStreaming();
          break;
        case 'stream_error':
        default:
          this.notifyError('errors.please_try_again');
          // eslint-disable-next-line no-console
          console.error('Streaming error:', data);
          break;
      }
      this.scrollToBottom();
    },
    handleTextarea(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (this.inputValue.trim() === '' || !this.canSendMessage) {
          return;
        }
        this.sendMessage(this.inputValue);
        this.inputValue = '';
      }
    },
    updateLastResponse(message) {
      const lastMessage = this.messages[this.messages.length - 1];
      if (lastMessage.type !== 'response') {
        this.messages.push({ text: message, type: 'response', timestamp: Date.now() });
        return;
      }
      const text = lastMessage.text + message;
      this.messages.pop();
      this.messages = [
        ...this.messages,
        { text, type: 'response', timestamp: Date.now() },
      ];
    },
    sendMessage(message) {
      if (!this.canSendMessage) {
        this.notifyWarning('データを選択してからご質問ください');
        return;
      }

      // 即座にUIを更新（送信完了を待たない）
      this.messages = [
        ...this.messages,
        { text: message, type: 'query', timestamp: Date.now() },
      ];
      this.streaming = true;
      this.inputValue = '';

      // 送信処理は非同期で実行、エラーのみcatchで管理
      this.connection.send({
        action: 'message',
        session_id: this.sessionId,
        message,
        user_id: this.$store.getters.getCurrentUser.id,
        place_id: this.place_id,
        event_ids: this.events.map(event => event.id),
      }).catch((error) => {
        // AbortErrorの場合はエラーメッセージを表示しない
        if (error.name === 'AbortError') {
          // 静かに失敗（ユーザーが意図的に新セッション開始）
          // eslint-disable-next-line no-console
          console.log('Send operation aborted due to new session');
          return;
        }

        // その他のエラーの場合は従来通りエラー処理
        this.messages = this.messages.filter(msg => !(msg.text === message && msg.type === 'query'));
        this.streaming = false;
        this.notifyError('errors.please_try_again');
        // eslint-disable-next-line no-console
        console.error('WebSocket error:', error);
      });
    },
    scrollToBottom() {
      const container = this.$refs.history;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleBeforeLeave() {
      this.isAnimating = true;
    },
    handleAfterLeave() {
      this.isAnimating = false;
    },
    addSystemMessage(messageKey) {
      const message = {
        text: this.$t(messageKey),
        type: 'system',
        timestamp: Date.now(),
      };
      this.messages = [...this.messages, message];
    },
    startNewSessionWithNotification() {
      // 最後のメッセージがシステムメッセージでない場合のみ追加
      const lastMessage = this.messages[this.messages.length - 1];
      if (!lastMessage || lastMessage.type !== 'system') {
        this.addSystemMessage('data_changed_notification');
      }
      this.sessionId = null;
      this.streaming = false;
    },
    isValidSessionResponse(data) {
      // stream_startは常に受け入れる（新セッション開始）
      if (data.type === 'stream_start') {
        return true;
      }

      // stream_chunk/stream_endは現在のsessionIdと一致する場合のみ
      if (!this.sessionId || data.session_id !== this.sessionId) {
        // eslint-disable-next-line no-console
        console.warn('Ignoring response from different session:', {
          expected: this.sessionId,
          received: data.session_id,
          type: data.type,
        });
        return false;
      }
      return true;
    },
    applyFocusFix(element) {
      // Vuetify 2.6.3のフォーカス管理バグに対する汎用的な公式回避策
      // position:fixed要素内のfocusable要素でのfocusinイベント問題を解決

      // 要素との相互作用が発生している間のみ有効化するフラグ
      let isElementInteracting = false;

      // 要素クリック開始時に相互作用フラグを設定
      element.addEventListener('mousedown', () => {
        isElementInteracting = true;
        // 短時間後に自動でフラグを解除（通常のフォーカス管理を妨げないため）
        setTimeout(() => {
          isElementInteracting = false;
        }, 200);
      });

      // Vuetify公式推奨回避策: focusinイベントの伝播を阻止
      // 特定条件（要素との相互作用中）でのみ実行
      element.addEventListener('focusin', (e) => {
        if (isElementInteracting) {
          // Vuetifyの内部focusinハンドラー（onFocusin）への伝播を阻止
          e.stopPropagation();
        }
      });
    },
  },
  watch: {
    messages() {
      this.scrollToBottom();
    },
    events(newEvents, oldEvents) {
      // 初回ロード時は通知しない
      if (!oldEvents || oldEvents.length === 0) {
        return;
      }

      // イベントIDの配列を比較してデータが変更されたかチェック
      const newIds = newEvents.map(event => event.id).sort();
      const oldIds = oldEvents.map(event => event.id).sort();

      const hasChanged = newIds.length !== oldIds.length ||
        newIds.some((id, index) => id !== oldIds[index]);

      if (hasChanged && this.messages.length > 0) {
        // ストリーミング中の場合は保留、そうでなければ即座に通知
        if (this.streaming) {
          this.pendingDataChange = true;
        } else {
          this.startNewSessionWithNotification();
        }
      }
    },
  },
};
</script>

<style scoped lang="sass">
.ai-chat-widget
  position: fixed
  right: 20px
  bottom: 10px
  z-index: 1000

  &__minimized
    min-width: 56px!important
    width: 56px!important
    height: 56px!important
    cursor: pointer
    border-radius: 50%
    background: white
    display: flex
    align-items: center
    justify-content: center
    transition: all 0.3s ease

    &:hover
      box-shadow: 0 6px 16px rgba(0,0,0,0.2) !important

    ::v-deep .v-icon
      font-size: 24px !important
      margin: 0 !important

  &__window
    width: 350px
    height: 500px
    background: white
    border-radius: 8px
    box-shadow: 0 4px 20px rgba(0,0,0,0.15)
    display: flex
    flex-direction: column
    overflow: hidden

  &__header
    padding: 12px 16px
    border-bottom: 1px solid #e0e0e0
    display: flex
    align-items: center
    justify-content: space-between
    background: #f5f5f5

  &__title
    font-size: 14px
    font-weight: 500
    color: #333

  &__controls
    display: flex
    gap: 4px

  &__history
    flex: 1
    overflow-y: auto
    padding: 16px
    scroll-behavior: smooth
    position: relative

  &__footer
    border-top: 1px solid #e0e0e0
    padding: 0
    background-color: #fff

  &__message
    padding: 8px 12px
    margin: 8px 0
    max-width: 90%
    font-size: 13px
    border-radius: 8px
    white-space: pre-line
    background-color: var(--ai-chat-background-color)
    line-height: 1.4

  &__response
    display: flex
    justify-content: flex-start
    --ai-chat-background-color: #d6e6f7

  &__query
    display: flex
    justify-content: flex-end
    --ai-chat-background-color: #eee

  &__system
    display: flex
    justify-content: center
    --ai-chat-background-color: #f0f8ff

  &__input
    width: 100%
    field-sizing: content
    resize: none
    outline: none
    border: none
    min-height: 40px
    max-height: 120px
    padding: 12px 16px
    font-size: 13px
    line-height: 1.4

    &:disabled
      background-color: #f5f5f5
      color: #999
      cursor: not-allowed

  &__disclaimer
    font-size: 10px
    color: #666
    margin: 8px 16px
    text-align: center
    line-height: 1.3

  &__presets
    position: absolute
    bottom: 16px
    left: 16px
    right: 16px
    text-align: center

// トランジション効果
.slide-up-enter-active
  transition: all 0.3s ease-out

.slide-up-leave-active
  transition: all 0.3s ease-in

.slide-up-enter
  transform: translateY(20px)
  opacity: 0

.slide-up-leave-to
  transform: translateY(20px)
  opacity: 0

// レスポンシブ対応
@media (max-width: 768px)
  .ai-chat-widget
    &__window
      width: calc(100vw - 50px)
      height: 70vh
      max-height: 600px

@media (max-width: 480px)
  .ai-chat-widget
    &__window
      width: calc(100vw - 40px)
      height: 80vh
      max-height: 500px

    &__header
      padding: 10px 12px

    &__title
      font-size: 13px

    &__history
      padding: 12px

    &__message
      font-size: 12px
      padding: 6px 10px

    &__input
      padding: 10px 12px
      font-size: 12px

    &__disclaimer
      font-size: 9px
      margin: 6px 12px
</style>
